if (!customElements.get('x-modal')) {
  class XModal extends HTMLElement {
    constructor() {
      super();
      this.modalId = null;
      this.container = null;
      this.closeBtn = null;
      this.isOpen = false;
      this._handleKeydown = this._handleKeydown.bind(this);
      this._handleDocumentClick = this._handleDocumentClick.bind(this);
    }

    connectedCallback() {
      this.modalId = this.getAttribute('data-modal-id');
      if (!this.modalId) return;

      this.container = this.querySelector(`.x-modal-container__${this.modalId}`);
      this.closeBtn = this.querySelector(`.x-modal-close__${this.modalId}`);

      if (!this.container) return;

      // Event Listeners internes
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        });
      }

      this.container.addEventListener('click', (e) => {
        if (e.target === this.container && !this.hasAttribute('data-no-backdrop-close')) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        }
      });

      // Initialisation des triggers externes (remplace le polling)
      // On utilise une référence liée pour pouvoir supprimer l'écouteur plus tard
      document.addEventListener('click', this._handleDocumentClick);

      // Vérification des paramètres d'URL pour ouverture automatique
      this._checkUrlParams();
    }

    disconnectedCallback() {
      window.removeEventListener('keydown', this._handleKeydown);
      document.removeEventListener('click', this._handleDocumentClick);
    }

    _handleDocumentClick(event) {
      // 1. Gestion des boutons avec la classe spécifique (Legacy)
      const triggerClass = `x-button__${this.modalId}`;
      const trigger = event.target.closest(`.${triggerClass}`);
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        this.open(trigger);
        return;
      }

      // 2. Gestion des liens <a> avec paramètres d'URL (Deep linking sans rechargement)
      const link = event.target.closest('a');
      if (link && link.href) {
        try {
          const url = new URL(link.href);
          const params = url.searchParams;

          // Vérifie si le lien cible cette modale spécifique
          if (params.get('modal') === 'true' && params.get('id') && params.get('id').includes(this.modalId)) {
            event.preventDefault();
            event.stopPropagation();
            this.open();
          }
        } catch (e) {
          // Ignore les URLs invalides
        }
      }
    }

    _checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const shouldOpen = urlParams.get('modal') === 'true';
      const targetId = urlParams.get('id');

      // L'ID dans l'URL est souvent sous la forme 'x-button__alert-modal'
      // Notre modalId est 'alert-modal'
      // On vérifie si l'ID de l'URL contient notre modalId
      if (shouldOpen && targetId && targetId.includes(this.modalId)) {
        this.open();
      }
    }

    open(trigger) {
      if (this.isOpen) return;
      this.isOpen = true;

      this.container.style.display = 'flex';
      // Force reflow
      this.container.offsetHeight;

      this.container.classList.add('fade-in');
      this.container.classList.remove('fade-out');

      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', this._handleKeydown);

      // `trigger` (l'élément cliqué) n'est fourni que par le chemin _handleDocumentClick
      // ci-dessus ; les autres appelants (_checkUrlParams, lien deep-link) passent undefined
      // → detail.trigger vaut null. Champ additif : les consommateurs existants de cet event
      // ne lisent que detail.modalId, donc aucun des 7 autres usages de cette modale n'est affecté.
      document.dispatchEvent(
        new CustomEvent('modal-opened', { detail: { modalId: this.modalId, trigger: trigger || null } }),
      );
    }

    close() {
      if (!this.isOpen) return;

      this.container.classList.add('fade-out');
      this.container.classList.remove('fade-in');

      document.dispatchEvent(new CustomEvent('modal-closed', { detail: { modalId: this.modalId } }));

      // On utilise { once: true } pour l'écouteur de transitionend si possible,
      // mais ici on garde le setTimeout pour correspondre au CSS transition: 0.3s
      // On ajoute une protection pour ne pas exécuter si l'état a changé entre temps
      setTimeout(() => {
        // Si la modale a été rouverte pendant l'animation de fermeture, on annule
        if (this.container.classList.contains('fade-in')) return;

        this.container.style.display = 'none';
        this.container.classList.remove('fade-out');
        this.isOpen = false;
        document.body.style.overflow = '';
        window.removeEventListener('keydown', this._handleKeydown);
      }, 300);
    }

    _handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      }
    }
  }

  customElements.define('x-modal', XModal);
}
